import {gql} from '@apollo/client';

import {writeFragmentToCache} from 'graphql-client/ApolloWrappers';
import FragmentWrapper from 'graphql-client/FragmentWrapper';
import * as Net from 'util/Net';

const AGENT_SKILL_IS_ENABLED_FRAGMENT = gql`
  fragment AgentSkillIsEnabled on AgentSkillNode {
    id
    isEnabled
  }
`;

interface AgentSkillIsEnabled {
  __typename: 'AgentSkillNode';
  id: string;
  isEnabled: boolean;
}

const AgentSkillIsEnabledFragment: FragmentWrapper<AgentSkillIsEnabled> = {
  fragment: AGENT_SKILL_IS_ENABLED_FRAGMENT,
  fragmentName: 'AgentSkillIsEnabled',
};

export function writeSkillIsEnabledToCache(skillId: string, isEnabled: boolean): void {
  writeFragmentToCache(AgentSkillIsEnabledFragment, {
    __typename: 'AgentSkillNode',
    id: skillId,
    isEnabled,
  });
}

export interface AgentSkillFile {
  id: string;
  virtualPath: string;
}

export interface SkillSummary {
  id: string;
  name: string;
  description: string;
  createdAt: string | null;
  isOwnedByCurrentUser: boolean;
  creatorName?: string;
  extensionDefinitionId: string | null;
  skillFiles: Array<AgentSkillFile>;
  isEnabled: boolean;
}

export const AGENT_SKILLS_QUERY = gql`
  query AgentSkillsQuery @engComponent(name: LLM) @skipAudit {
    agentSkills {
      id
      name
      description
      createdAt
      creatorId
      creatorName
      extensionDefinitionId
      isEnabled
      skillFiles {
        id
        virtualPath
      }
    }
  }
`;

export const AGENT_SKILL_VERSIONS_QUERY = gql`
  query AgentSkillVersionsQuery($id: StrictString!) @engComponent(name: LLM) @skipAudit {
    agentSkill(id: $id) {
      __typename
      ... on AgentSkillNode {
        id
        versions {
          id
          name
          description
          createdAt
          creatorId
          creatorName
          oauth2ClientId
          oauth2ClientName
          isCurrent
          files {
            name
            s3ExternalFileId
            mediaType
          }
        }
      }
      ... on InaccessibleInfoNode {
        invalidId
        canRequestAccess
      }
    }
  }
`;

interface AgentSkillVersionsNodeData {
  __typename: 'AgentSkillNode';
  id: string;
  versions: Array<{
    id: string;
    name: string;
    description: string;
    createdAt: string;
    creatorId: string | null;
    creatorName: string | null;
    oauth2ClientId: string | null;
    oauth2ClientName: string | null;
    isCurrent: boolean;
    files: Array<{
      name: string;
      s3ExternalFileId: string;
      mediaType: string;
    }>;
  }>;
}

interface AgentSkillInaccessibleData {
  __typename: 'InaccessibleInfoNode';
  invalidId: string;
  canRequestAccess: boolean;
}

export type AgentSkillVersionsQueryResult = AgentSkillVersionsNodeData | AgentSkillInaccessibleData;

export interface AgentSkillVersionsQueryData {
  agentSkill: AgentSkillVersionsQueryResult | null;
}

export const AGENT_SKILL_VERSION_FILE_PRESIGNED_URL_QUERY = gql`
  query AgentSkillVersionFilePresignedUrlQuery($s3ExternalFileId: StrictString!)
  @engComponent(name: LLM)
  @skipAudit {
    agentSkillVersionFilePresignedUrl(s3ExternalFileId: $s3ExternalFileId)
  }
`;

export interface AgentSkillVersionFilePresignedUrlQueryData {
  agentSkillVersionFilePresignedUrl: string | null;
}

export interface AgentSkillVersionFilePresignedUrlQueryVariables {
  s3ExternalFileId: string;
}

export const AGENT_SKILL_FILE_QUERY = gql`
  query AgentSkillFileQuery($id: StrictString!) @engComponent(name: LLM) @skipAudit {
    agentSkillFile(id: $id) {
      id
      virtualPath
      s3LocationId
      presignedUrl
    }
  }
`;

export interface AgentSkillFileQueryData {
  agentSkillFile: {
    id: string;
    virtualPath: string;
    presignedUrl: string | null;
  } | null;
}

export interface AgentSkillFileQueryVariables {
  id: string;
}

export interface AgentSkillsQueryData {
  agentSkills: Array<{
    id: string;
    name: string;
    description: string;
    createdAt: string;
    creatorId: string | null;
    creatorName: string | null;
    extensionDefinitionId: string | null;
    isEnabled: boolean;
    skillFiles: Array<AgentSkillFile>;
  }>;
}

export function bulkCreateSkills(s3Locations: Array<string>): Promise<{skillIds: Array<string>}> {
  return Net.postJSONPromise({
    url: '/1/api/llm/agent-skills:bulk-create',
    data: {s3Locations},
  });
}

export function updateSkill(
  skillId: string,
  updates: {name?: string; description?: string}
): Promise<{skillId: string}> {
  return Net.patchJSONPromise({url: `/1/api/llm/agent-skills/${skillId}`, data: updates});
}

export async function archiveSkill(skillId: string, reason?: string): Promise<void> {
  await Net.postJSONPromise({
    url: `/1/api/llm/agent-skills/${skillId}:archive`,
    data: {reason: reason ?? ''},
  });
}

export async function unarchiveSkill(skillId: string): Promise<void> {
  await Net.postJSONPromise({url: `/1/api/llm/agent-skills/${skillId}:unarchive`, data: {}});
}

export async function enableSkill(skillId: string): Promise<void> {
  await Net.postJSONPromise({url: `/1/api/llm/agent-skills/${skillId}:enable`, data: {}});
}

export async function disableSkill(skillId: string): Promise<void> {
  await Net.postJSONPromise({url: `/1/api/llm/agent-skills/${skillId}:disable`, data: {}});
}

export interface SkillVersionFile {
  name: string;
  s3ExternalFileId: string;
  mediaType: string;
}

export interface SkillVersion {
  id: string;
  name: string;
  description: string;
  createdAt: string;
  creatorId: string | null;
  creatorName: string | null;
  oauth2ClientId: string | null;
  oauth2ClientName: string | null;
  isCurrent: boolean;
  files: Array<SkillVersionFile>;
}

export function revertSkillToVersion(
  skillId: string,
  versionId: string
): Promise<{skillId: string; versionId: string}> {
  return Net.postJSONPromise({
    url: `/1/api/llm/agent-skills/${skillId}:revert-to-version`,
    data: {versionId},
  });
}
